import React, { useState, useRef } from "react";
import styles from "./LeafRiver.module.css";

/**
 * LeafRiverHtml — interactive leaf river for therapy sessions
 * - Filenames / component names in English
 * - Visible strings in Portuguese
 * - No persistence (no localStorage / no server)
 */
export default function LeafRiverHtml({
  title = "Folhas no Riacho",
  hint = "Observe os pensamentos como folhas — deixe-os passar.",
  initialAnimate = true,
  initialSpeed = 1.0,
}) {
  const [running, setRunning] = useState(Boolean(initialAnimate));
  const [speed, setSpeed] = useState(Number(initialSpeed));
  const [editMode, setEditMode] = useState(false);
  const [leafTexts, setLeafTexts] = useState([
    "pensamento...",
    "ex.: preocupação",
    "memória",
  ]);
  const [resetKey, setResetKey] = useState(0);
  const stageRef = useRef(null);

  const MIN_SPEED = 0.3;
  const MAX_SPEED = 3.0;
  const STEP = 0.2;

  const increaseSpeed = () =>
    setSpeed((s) => Math.min(MAX_SPEED, +(s + STEP).toFixed(2)));
  const decreaseSpeed = () =>
    setSpeed((s) => Math.max(MIN_SPEED, +(s - STEP).toFixed(2)));

  const onEditChange = (index, value) => {
    setLeafTexts((arr) => {
      const copy = [...arr];
      copy[index] = value;
      return copy;
    });
  };

  const copyThoughts = async () => {
    const text = leafTexts.map((t, i) => `Folha ${i + 1}: ${t}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      // small visual feedback: temporary aria-live message (or console)
      console.info("Pensamentos copiados para a área de transferência.");
    } catch (e) {
      console.warn("Não foi possível copiar (permissão).", e);
    }
  };

  const resetLeaves = () => {
    // incrementing key will remount the leaves container and restart animations
    setResetKey((k) => k + 1);
  };

  const enterFullscreen = async () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (e) {
        /* ignore */
      }
      return;
    }
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) await el.msRequestFullscreen();
    } catch (e) {
      console.warn("Fullscreen não suportado ou bloqueado.", e);
    }
  };

  // container inline style: set CSS var --speed so CSS calc adjusts durations
  const containerStyle = { ["--speed"]: String(speed) };

  return (
    <div
      ref={stageRef}
      className={styles.stage}
      role="img"
      aria-label={`${title} — metáfora visual para pensamentos passando`}
      data-animate={running ? "true" : "false"}
      style={containerStyle}
    >
      <div className={styles.panel}>
        <strong>{title}</strong>
        <div className={styles.hint}>{hint}</div>
      </div>

      <div className={styles.stream} aria-hidden="true" />

      {/* leaves container uses key to support reset */}
      <div key={resetKey} className={styles.leaves} aria-hidden="false">
        <div
          className={`${styles.leaf} ${styles.l1}`}
          role="group"
          aria-label="Folha 1"
        >
          <span
            className={styles.leafText}
            contentEditable
            suppressContentEditableWarning
            aria-label="Texto da folha 1"
          >
            {leafTexts[0]}
          </span>
        </div>

        <div
          className={`${styles.leaf} ${styles.l2}`}
          role="group"
          aria-label="Folha 2"
        >
          <span
            className={styles.leafText}
            contentEditable
            suppressContentEditableWarning
            aria-label="Texto da folha 2"
          >
            {leafTexts[1]}
          </span>
        </div>

        <div
          className={`${styles.leaf} ${styles.l3}`}
          role="group"
          aria-label="Folha 3"
        >
          <span
            className={styles.leafText}
            contentEditable
            suppressContentEditableWarning
            aria-label="Texto da folha 3"
          >
            {leafTexts[2]}
          </span>
        </div>
      </div>

      {/* controls */}
      <div
        className={styles.controls}
        role="toolbar"
        aria-label="Controles de animação"
      >
        <div className={styles.controlRow}>
          <button
            type="button"
            className={styles.controlBtn}
            onClick={() => setRunning(true)}
            aria-pressed={running}
            title="Iniciar"
            aria-label="Iniciar animação"
          >
            Iniciar
          </button>

          <button
            type="button"
            className={styles.controlBtn}
            onClick={() => setRunning(false)}
            aria-pressed={!running}
            title="Pausar"
            aria-label="Pausar animação"
          >
            Pausar
          </button>

          <div className={styles.separator} aria-hidden="true" />

          <button
            type="button"
            className={styles.controlBtn}
            onClick={decreaseSpeed}
            aria-label="Diminuir velocidade"
            title="Diminuir velocidade"
          >
            −
          </button>

          <div className={styles.speedLabel} aria-live="polite">
            Velocidade: {Math.round(speed * 100)}%
          </div>

          <button
            type="button"
            className={styles.controlBtn}
            onClick={increaseSpeed}
            aria-label="Aumentar velocidade"
            title="Aumentar velocidade"
          >
            +
          </button>

          <div className={styles.separator} aria-hidden="true" />

          <button
            type="button"
            className={styles.controlBtn}
            onClick={() => setEditMode((v) => !v)}
            aria-pressed={editMode}
            aria-label="Alternar modo de edição"
            title={
              editMode ? "Sair do modo de edição" : "Editar textos das folhas"
            }
          >
            {editMode ? "Fechar edição" : "Editar textos"}
          </button>

          <button
            type="button"
            className={styles.controlBtn}
            onClick={copyThoughts}
            aria-label="Copiar pensamentos"
            title="Copiar pensamentos"
          >
            Copiar pensamentos
          </button>

          <button
            type="button"
            className={styles.controlBtn}
            onClick={resetLeaves}
            aria-label="Recolocar folhas"
            title="Recolocar folhas"
          >
            Recolocar
          </button>

          <button
            type="button"
            className={styles.controlBtn}
            onClick={enterFullscreen}
            aria-label="Fullscreen"
            title="Tela cheia (ESC para sair)"
          >
            Fullscreen
          </button>
        </div>

        {/* edit area: inputs appear only when editMode === true */}
        {editMode && (
          <div
            className={styles.editArea}
            aria-label="Editor de textos das folhas"
          >
            <label className={styles.editLabel}>
              Folha 1:
              <input
                className={styles.editInput}
                value={leafTexts[0]}
                onChange={(e) => onEditChange(0, e.target.value)}
                aria-label="Texto para folha 1"
              />
            </label>

            <label className={styles.editLabel}>
              Folha 2:
              <input
                className={styles.editInput}
                value={leafTexts[1]}
                onChange={(e) => onEditChange(1, e.target.value)}
                aria-label="Texto para folha 2"
              />
            </label>

            <label className={styles.editLabel}>
              Folha 3:
              <input
                className={styles.editInput}
                value={leafTexts[2]}
                onChange={(e) => onEditChange(2, e.target.value)}
                aria-label="Texto para folha 3"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
